// NavigationContext.js - Extended to protect multiple routes
import React, { createContext, useContext, useState } from "react";

const NavigationContext = createContext();

export const useNavigation = () => {
  const context = useContext(NavigationContext);
  if (!context) {
    throw new Error("useNavigation must be used within a NavigationProvider");
  }
  return context;
};

export const NavigationProvider = ({ children }) => {
  // Track access permissions for different pages
  const [accessPermissions, setAccessPermissions] = useState({
    canAccessSuccess: false,
    canAccessApplicationForm: false,
    canAccessJobDetails: false,
  });

  // Allow access to Success page (after form submission)
  const allowSuccessAccess = () => {
    setAccessPermissions((prev) => ({
      ...prev,
      canAccessSuccess: true,
    }));
  };

  // Allow access to Application Form (from job details or home page)
  const allowApplicationFormAccess = () => {
    setAccessPermissions((prev) => ({
      ...prev,
      canAccessApplicationForm: true,
    }));
  };

  // Allow access to Job Details (from home page job listings)
  const allowJobDetailsAccess = () => {
    setAccessPermissions((prev) => ({
      ...prev,
      canAccessJobDetails: true,
    }));
  };

  // Reset all access permissions
  const resetAllAccess = () => {
    setAccessPermissions({
      canAccessSuccess: false,
      canAccessApplicationForm: false,
      canAccessJobDetails: false,
    });
  };

  // Reset specific access
  const resetSuccessAccess = () => {
    setAccessPermissions((prev) => ({
      ...prev,
      canAccessSuccess: false,
    }));
  };

  const resetApplicationFormAccess = () => {
    setAccessPermissions((prev) => ({
      ...prev,
      canAccessApplicationForm: false,
    }));
  };

  const resetJobDetailsAccess = () => {
    setAccessPermissions((prev) => ({
      ...prev,
      canAccessJobDetails: false,
    }));
  };

  return (
    <NavigationContext.Provider
      value={{
        // Access state
        canAccessSuccess: accessPermissions.canAccessSuccess,
        canAccessApplicationForm: accessPermissions.canAccessApplicationForm,
        canAccessJobDetails: accessPermissions.canAccessJobDetails,

        // Allow access functions
        allowSuccessAccess,
        allowApplicationFormAccess,
        allowJobDetailsAccess,

        // Reset functions
        resetSuccessAccess,
        resetApplicationFormAccess,
        resetJobDetailsAccess,
        resetAllAccess,
      }}
    >
      {children}
    </NavigationContext.Provider>
  );
};
